import { Box, Checkbox, useMediaQuery, useTheme } from "@mui/material";
import { ReactNode, useMemo } from "react";
import TimeSlotToggle from "../TimeslotToggle";
import { CHECKBOX_STATE, TimeSlotItem } from "../types";
import { useGenerateTimeSlotItems } from "../hooks/useGenerateTimeSlotItems";

interface ISchedulePeriodRowProps {
    label: ReactNode;
    generalValue: number;
    rowIndex: number;
    onChange: (value: number) => void;
}

const weekDayLabels: string[] = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export default function SchedulePeriodRow({ label, generalValue, rowIndex, onChange }: ISchedulePeriodRowProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

    const timeSlotItems = useGenerateTimeSlotItems(rowIndex);
    const isFirstRow = rowIndex === 0;

    const weekDays = useMemo(
        () =>
            timeSlotItems
                .filter((item: TimeSlotItem) => item.isWeekDay)
                .map((item: TimeSlotItem) => ({
                    ...item,
                    isChecked: (generalValue & item.value) === item.value,
                })),
        [timeSlotItems, generalValue]
    );

    const checkboxState = useMemo(() => {
        const checkedCount = weekDays.filter((x) => x.isChecked).length;

        if (checkedCount === 0) return CHECKBOX_STATE.Empty;
        if (checkedCount === weekDays.length) return CHECKBOX_STATE.Checked;

        return CHECKBOX_STATE.Indeterminate;
    }, [weekDays]);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newBitValue = weekDays.reduce((acc, item) => {
            return event.target.checked
                ? acc | item.value // Set bits
                : acc & ~item.value; // Clear bits
        }, generalValue);

        onChange(newBitValue);
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: isMobile ? "wrap" : "unset",
                gap: isMobile ? 0 : 1,
                height: isMobile ? "auto" : "34px",
            }}
        >
            {label}
            <Checkbox
                size="small"
                indeterminate={checkboxState === CHECKBOX_STATE.Indeterminate}
                checked={checkboxState === CHECKBOX_STATE.Checked}
                onChange={(e) => handleCheckboxChange(e)}
                sx={{ marginLeft: isMobile ? "14px" : "8px" }}
            />
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    width: "100%",
                    position: isFirstRow && !isMobile ? "relative" : "static",
                    bottom: isFirstRow && !isMobile ? "10px" : "0",
                }}
            >
                {timeSlotItems.map((timeSlotItem: TimeSlotItem, index: number) => (
                    <TimeSlotToggle
                        key={timeSlotItem.key}
                        value={timeSlotItem.value}
                        generalValue={generalValue}
                        onChange={onChange}
                        isMobile={isMobile}
                        label={weekDayLabels[index]}
                        isFirstRow={isFirstRow}
                    />
                ))}
            </Box>
        </Box>
    );
}
